/**
 * What each screen lists: its rows, in order, with their values as of the settings now. The ids are
 * what the model acts on (`start1`, `options`, `key.0.jump`, `musicVolume`, `back` ...).
 */

import type { Settings } from '../../settings';
import type { MenuScreen } from './index';
import { OPTION_ROWS } from './options';
import { ACTION_LABELS, KEY_ACTIONS, type KeyAction, keysText, type PlayerIndex } from './rebind';

/** One row as a screen shows it. */
export interface MenuRow {
  id: string;
  label: string;
  kind: 'action' | 'option' | 'key';
  /** The value right of the label (options, keys). */
  value?: string;
  /** 0..1, drawn as a bar before the value. */
  bar?: number;
  disabled?: boolean;
  /** The value is a warning (PRACTICE). */
  warn?: boolean;
  /** The version on the title while its changes are unseen. */
  isNew?: boolean;
}

/** What the rows depend on beyond the settings. */
export interface RowContext {
  version: string;
  changesNew: boolean;
  /** The controls row waiting for a key. */
  listening: { player: PlayerIndex; action: KeyAction } | null;
}

/** The value a controls row shows while it waits for a key. */
export const WAITING = 'PRESS A KEY';

const row = (id: string, label: string): MenuRow => ({ id, label, kind: 'action' });

function keyRow(s: Settings, p: PlayerIndex, a: KeyAction, ctx: RowContext): MenuRow {
  const waiting = ctx.listening?.player === p && ctx.listening.action === a;
  const value = waiting ? WAITING : keysText(s.keys[p][a]);
  return { id: `key.${p}.${a}`, label: ACTION_LABELS[a], kind: 'key', value };
}

export function screenRows(screen: MenuScreen | null, s: Settings, ctx: RowContext): MenuRow[] {
  switch (screen) {
    case 'title':
      return [
        row('start1', '1 PLAYER'), row('start2', '2 PLAYERS'), row('options', 'OPTIONS'),
        row('controls', 'CONTROLS'), row('credits', 'CREDITS'),
        { ...row('changes', `V${ctx.version.replace(/^v/i, '')}`), isNew: ctx.changesNew },
      ];
    case 'pause':
      return [
        row('resume', 'CONTINUE'), row('options', 'OPTIONS'), row('controls', 'CONTROLS'), row('quit', 'QUIT TO TITLE'),
      ];
    case 'options':
      return [
        ...OPTION_ROWS.map((o): MenuRow => ({ id: o.id, label: o.label, kind: 'option', ...o.view(s) })),
        row('resetOptions', 'RESET TO DEFAULTS'), row('back', 'BACK'),
      ];
    case 'controls':
      return [
        ...([0, 1] as const).flatMap((p) => KEY_ACTIONS.map((a) => keyRow(s, p, a, ctx))),
        row('resetKeys', 'RESET TO DEFAULTS'), row('back', 'BACK'),
      ];
    case 'credits':
    case 'changes':
      return [row('back', 'BACK')];
    default:
      return [];
  }
}
